import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import {
  ChangePassword,
  ForgotPasswordRequest,
  GetCookieConsent,
  GetEmailConsent,
  HideCookieConsentModal,
  Login,
  LoginFailure,
  Logout,
  ResetPassword,
  SetEmail,
  ShowCookieConsentModal,
  StartLoginSession,
  StartNewChangePasswordSession,
  StartNewForgotPasswordSession,
  StartNewResetPasswordSession,
  UpdateCookieConsent,
  UpdateEmailConsent,
  UpdateLocalCookieConsent
} from './account.actions';
import { AccountService, CookieConsentService, EmailConsentService, ForgotPasswordService } from '@api/services';
import { CookieConsentCustomerDto } from '@api/models/cookie-consent-customer-dto';
import { LoadCart } from '@app/state/cart/cart.actions';
import { map } from 'rxjs';

export class AccountStateModel {
  isAuthenticated: boolean;
  isInvalidPassword: boolean;
  errors: string[];
  isEmailSubmitted: boolean;
  email: string | null;
  isPasswordChangeSubmitted: boolean;
  passwordResetFailed: boolean;
  passwordResetSuccess: boolean;
  cookieConsent: CookieConsentCustomerDto;
  showCookieConsentModal: boolean;
  acceptEmails?: boolean;
}

@State<AccountStateModel>({
  name: 'account',
  defaults: {
    isAuthenticated: false,
    isInvalidPassword: false,
    errors: [],
    isEmailSubmitted: false,
    email: null,
    isPasswordChangeSubmitted: false,
    passwordResetFailed: false,
    passwordResetSuccess: false,
    cookieConsent: null,
    showCookieConsentModal: false,
    acceptEmails: null
  }
})
@Injectable()
export class AccountState {
  private store = inject(Store);
  private forgotPasswordService = inject(ForgotPasswordService);
  private accountService = inject(AccountService);
  private cookieConsentService = inject(CookieConsentService);
  private emailConsentService = inject(EmailConsentService);

  @Selector()
  static isAuthenticated(state: AccountStateModel): boolean {
    return state.isAuthenticated;
  }

  @Selector()
  static selectIsInvalidPassword(state: AccountStateModel): boolean {
    return state.isInvalidPassword;
  }

  @Selector()
  static selectErrors(state: AccountStateModel): string[] {
    return state.errors;
  }

  @Selector()
  static selectIsEmailSubmitted(state: AccountStateModel): boolean {
    return state.isEmailSubmitted;
  }

  @Selector()
  static selectEmail(state: AccountStateModel): string {
    return state.email;
  }

  @Selector()
  static selectIsChangeSubmitted(state: AccountStateModel): boolean {
    return state.isPasswordChangeSubmitted;
  }

  @Selector()
  static selectResetPasswordFailed(state: AccountStateModel): boolean {
    return state.passwordResetFailed;
  }

  @Selector()
  static selectResetPasswordSuccess(state: AccountStateModel): boolean {
    return state.passwordResetSuccess;
  }

  @Selector()
  static selectCookieConsent(state: AccountStateModel): CookieConsentCustomerDto {
    return state.cookieConsent;
  }

  @Selector()
  static selectShowCookieConsentModal(state: AccountStateModel): boolean {
    return state.showCookieConsentModal;
  }

  @Selector()
  static selectEmailConsent(state: AccountStateModel): boolean {
    return state.acceptEmails;
  }

  @Action(Login)
  private login({ patchState }: StateContext<AccountStateModel>) {
    this.store.dispatch(new LoadCart());
    return patchState({ isAuthenticated: true });
  }

  @Action(Logout)
  private logout({ patchState }: StateContext<AccountStateModel>) {
    return patchState({ isAuthenticated: false });
  }

  @Action(StartLoginSession)
  private startLoginSession(ctx: StateContext<AccountStateModel>) {
    return ctx.patchState({ isInvalidPassword: false, errors: [] });
  }

  @Action(LoginFailure)
  private loginFailure(ctx: StateContext<AccountStateModel>, payload: LoginFailure) {
    return ctx.patchState({
      isInvalidPassword: payload.loginResponse.isInvalidPassword,
      errors: payload.loginResponse.errors
    });
  }

  @Action(ForgotPasswordRequest)
  private forgotPasswordRequest(ctx: StateContext<AccountStateModel>, action: ForgotPasswordRequest) {
    return this.forgotPasswordService
      .apiIdentityAccountForgotpasswordPost({ body: { email: action.email } })
      .subscribe((response) => {
        if (response.hasError) {
          return ctx.patchState({ errors: response.errors, isEmailSubmitted: false });
        } else {
          return ctx.patchState({ email: response.email, isEmailSubmitted: true });
        }
      });
  }

  @Action(StartNewForgotPasswordSession)
  private startNewForgotPasswordSession(ctx: StateContext<AccountStateModel>) {
    return ctx.patchState({ isEmailSubmitted: false, errors: [] });
  }

  @Action(StartNewChangePasswordSession)
  private startNewChangePasswordSession(ctx: StateContext<AccountStateModel>) {
    return ctx.patchState({ isInvalidPassword: false, isPasswordChangeSubmitted: false, errors: [] });
  }

  @Action(ChangePassword)
  private changePassword(ctx: StateContext<AccountStateModel>, action: ChangePassword) {
    return this.accountService.apiIdentityAccountChangepasswordPut({ body: action.payload }).pipe(
      map((response) => {
        if (response.hasError) {
          ctx.patchState({ isInvalidPassword: true, isPasswordChangeSubmitted: false });
        } else {
          ctx.patchState({ isInvalidPassword: false, isPasswordChangeSubmitted: true });
        }
      })
    );
  }

  @Action(StartNewResetPasswordSession)
  private startNewResetPasswordSession(ctx: StateContext<AccountStateModel>) {
    return ctx.patchState({ passwordResetFailed: false, passwordResetSuccess: false, errors: [] });
  }

  @Action(ResetPassword)
  private resetPassword(ctx: StateContext<AccountStateModel>, action: ResetPassword) {
    return this.forgotPasswordService
      .apiIdentityAccountResetpasswordPost({ body: action.payload })
      .subscribe((response) => {
        if (response == null) {
          return ctx.patchState({
            passwordResetFailed: true,
            errors: ['Er is iets misgelopen. Gelieve opnieuw te proberen.']
          });
        }
        if (response.hasError) {
          return ctx.patchState({ passwordResetFailed: true, errors: response.errors });
        } else {
          return ctx.patchState({ passwordResetSuccess: true });
        }
      });
  }

  @Action(GetCookieConsent)
  private getCookieConsent(ctx: StateContext<AccountStateModel>) {
    return this.cookieConsentService.apiIdentityCookieconsentGet().subscribe((response) => {
      return ctx.patchState({ cookieConsent: response });
    });
  }

  @Action(UpdateCookieConsent)
  private updateCookieConsent(ctx: StateContext<AccountStateModel>, action: UpdateCookieConsent) {
    return this.cookieConsentService.apiIdentityCookieconsentPut({ body: action.payload });
  }

  @Action(UpdateLocalCookieConsent)
  private updateLocalCookieConsent(ctx: StateContext<AccountStateModel>, action: UpdateLocalCookieConsent) {
    return ctx.patchState({ cookieConsent: action.payload as CookieConsentCustomerDto });
  }

  @Action(ShowCookieConsentModal)
  private showCookieConsentModal(ctx: StateContext<AccountStateModel>) {
    return ctx.patchState({ showCookieConsentModal: true });
  }

  @Action(HideCookieConsentModal)
  private hideCookieConsentModal(ctx: StateContext<AccountStateModel>) {
    return ctx.patchState({ showCookieConsentModal: false });
  }

  @Action(GetEmailConsent)
  private getEmailConsent(ctx: StateContext<AccountStateModel>) {
    return this.emailConsentService.apiIdentityEmailconsentGet().subscribe((response) => {
      return ctx.patchState({ acceptEmails: response.accept });
    });
  }

  @Action(UpdateEmailConsent)
  private updateEmailConsent(ctx: StateContext<AccountStateModel>, action: UpdateEmailConsent) {
    return this.emailConsentService.apiIdentityEmailconsentPut({ body: { accept: action.payload.accept } });
  }

  @Action(SetEmail)
  private setEmail({ patchState }: StateContext<AccountStateModel>, action: SetEmail) {
    return patchState({ email: action.email });
  }
}
